import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  Bot,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FilterX,
  History,
  Loader2,
  RotateCw,
  Search,
  SkipForward,
  Upload,
  UserCog,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { formatDateString } from '../../../src/lib/janelaCobranca';
import {
  CAMPOS_DATA,
  FILTROS_VAZIOS,
  ORDEM_PADRAO,
  ROTULO_MOTIVO_ROBO,
  SITUACOES,
  URGENCIAS,
  chaveData,
  chaveFilaRobo,
  classificarStatusRateio,
  contarPorSituacao,
  dadosTitularMudaram,
  diasParaLimite,
  filtrarCobrancas,
  mesesIniciaisDaLista,
  motivoDoRobo,
  ordenarCobrancas,
  ordenarMeses,
  rotuloSituacao,
  situacaoDaCobranca,
  statusRoboDaCobranca,
  urgenciaDaCobranca,
  type CampoData,
  type CampoOrdem,
  type FiltrosLista,
  type Ordem,
  type SituacaoCobranca,
  type Urgencia,
} from '../lib/janelaFaturamentoLista';
import { ChipFaturaAtrasada, ChipImpressaNaHora, ChipMotivoRobo, ChipPrazoCurto, ChipStatusRateio } from './ChipsCobranca';

// Visualização em lista da Janela de Faturamento/Cobrança. Recebe os itens já
// mapeados pelo PendentesCalculoModal (janela, data limite, responsável) e as
// ações do modal — os sub-modais (reagendar, transferir, pular) continuam lá.

const LOTE_RENDERIZACAO = 150;

interface Props {
  itens: any[];
  analysts: { id: string; full_name?: string; email?: string }[];
  responsavel: string;
  onResponsavelChange: (id: string) => void;
  filaRobo: Map<string, any>;
  enqueuingForUc: string | null;
  uploadingForUc: string | null;
  uploadSuccessUc: string | null;
  podeTransferir: (item: any) => boolean;
  onCalcular: (item: any) => void;
  onVisualizar: (item: any) => void;
  onReagendar: (item: any) => void;
  onVerReagendamento: (item: any) => void;
  onTransferir: (item: any) => void;
  onPular: (item: any) => void;
  onEquatorial: (item: any, modo: 'mes_especifico' | 'ultima_disponivel') => void;
  onRetentarRobo: (queueId: string, item: any) => void;
  onVerHistoricoRobo: (filaEntrada: any) => void;
  onAtualizarDadosTitular: (item: any, tentativa: any) => void;
  onUpload: (uc: string) => void;
  onExportado?: (ok: boolean, mensagem: string) => void;
}

const COR_SITUACAO: Record<SituacaoCobranca, string> = {
  sem_fatura: 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200',
  a_calcular: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  calculado: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  emitido: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  enviado: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  pago: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  nao_precisa_calcular: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  cancelado: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 line-through',
};

const COR_URGENCIA: Record<Urgencia, { barra: string; chip: string }> = {
  critical: { barra: 'bg-red-500', chip: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  warning: { barra: 'bg-amber-400', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  sem_previsao: { barra: 'bg-amber-300', chip: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  normal: { barra: 'bg-[#5F6C37]/40', chip: 'bg-[#5F6C37]/10 text-[#5F6C37] dark:text-green-300' },
};

const hojeLocal = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const somarDias = (d: Date, n: number) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const fmt = (d: Date | null | undefined) => (d && !isNaN(d.getTime()) ? formatDateString(d) : '—');

const credenciaisRobo = (item: any) => {
  const cpfCnpj = String(item.ucData?.cpf_cnpj_titular || item.cpf_cnpj || '').replace(/\D/g, '');
  const temDoc = cpfCnpj.length >= 11;
  const temData = cpfCnpj.length === 11 ? !!item.ucData?.data_nascimento_titular : true;
  return { temDoc, temData, completo: temDoc && temData };
};

// ── Seleção múltipla de meses ────────────────────────────────────────────────

const SeletorMeses: React.FC<{ opcoes: string[]; valor: string[]; onChange: (v: string[]) => void }> = ({ opcoes, valor, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const fechar = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setAberto(false); };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);

  const alternar = (mes: string) =>
    onChange(valor.includes(mes) ? valor.filter((m) => m !== mes) : ordenarMeses([...valor, mes]));

  const rotulo = valor.length === 0 ? 'Todos os meses' : valor.length <= 2 ? valor.join(', ') : `${valor.length} meses`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 text-sm rounded-xl px-3 py-2 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 whitespace-nowrap"
      >
        <Calendar size={14} className="text-gray-400" />
        <span className="font-medium">{rotulo}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {aberto && (
        <div className="absolute z-20 mt-1 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2">
          <div className="flex justify-between px-2 pb-2 mb-1 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold">
            <button type="button" onClick={() => onChange([...opcoes])} className="text-[#5F6C37] hover:underline">Marcar todos</button>
            <button type="button" onClick={() => onChange([])} className="text-gray-400 hover:underline">Limpar</button>
          </div>
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {[...opcoes].reverse().map((mes) => (
              <label key={mes} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm text-gray-800 dark:text-gray-200">
                <input type="checkbox" checked={valor.includes(mes)} onChange={() => alternar(mes)} className="accent-[#5F6C37]" />
                {mes}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Componente ───────────────────────────────────────────────────────────────

const JanelaFaturamentoLista: React.FC<Props> = (props) => {
  const { itens, analysts, responsavel, onResponsavelChange, filaRobo } = props;

  const meses = useMemo(() => ordenarMeses(itens.map((i) => i.expectedMonth)), [itens]);
  const rateios = useMemo(
    () => Array.from(new Set(itens.map((i) => i.rateioNome).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), 'pt-BR', { numeric: true })),
    [itens],
  );

  // Abre no mês mais recente + meses anteriores ainda com pendência (mesmas
  // pendências dos cards); o usuário amplia ou reduz pelo seletor.
  const [filtrosLocais, setFiltrosLocais] = useState<Omit<FiltrosLista, 'responsavel'>>(() => ({
    ...FILTROS_VAZIOS,
    meses: mesesIniciaisDaLista(itens),
  }));
  const mesesIniciados = useRef(meses.length > 0);
  useEffect(() => {
    if (mesesIniciados.current || !meses.length) return;
    mesesIniciados.current = true;
    setFiltrosLocais((f) => ({ ...f, meses: mesesIniciaisDaLista(itens) }));
  }, [meses, itens]);

  const filtros: FiltrosLista = useMemo(() => ({ ...filtrosLocais, responsavel }), [filtrosLocais, responsavel]);
  const setFiltro = <K extends keyof FiltrosLista>(chave: K, valor: FiltrosLista[K]) => {
    if (chave === 'responsavel') onResponsavelChange(valor as string);
    else setFiltrosLocais((f) => ({ ...f, [chave]: valor }));
  };

  const [ordem, setOrdem] = useState<Ordem>(ORDEM_PADRAO);
  const [limiteLinhas, setLimiteLinhas] = useState(LOTE_RENDERIZACAO);

  const hoje = useMemo(hojeLocal, []);

  // Contagem dos chips de situação ignora o próprio filtro de situação, senão
  // marcar um chip zeraria todos os outros.
  const baseSemSituacao = useMemo(
    () => filtrarCobrancas(itens, { ...filtros, situacoes: [] }, filaRobo),
    [itens, filtros, filaRobo],
  );
  const contagemSituacao = useMemo(() => contarPorSituacao(baseSemSituacao), [baseSemSituacao]);

  const linhas = useMemo(() => {
    const filtradas = filtros.situacoes.length
      ? baseSemSituacao.filter((i) => filtros.situacoes.includes(situacaoDaCobranca(i)))
      : baseSemSituacao;
    return ordenarCobrancas(filtradas, ordem);
  }, [baseSemSituacao, filtros.situacoes, ordem]);

  const resumo = useMemo(() => {
    let pendentes = 0;
    let criticas = 0;
    let vencidas = 0;
    for (const l of linhas) {
      if (l.isCalculada) continue;
      pendentes++;
      if (urgenciaDaCobranca(l) === 'critical') criticas++;
      const dias = diasParaLimite(l, hoje);
      if (dias !== null && dias < 0) vencidas++;
    }
    return { pendentes, criticas, vencidas };
  }, [linhas, hoje]);

  // Renderização progressiva: a lista não tem teto, mas montar milhares de linhas
  // de uma vez trava o modal. Carrega mais ao chegar perto do fim da rolagem.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelaRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    setLimiteLinhas(LOTE_RENDERIZACAO);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [filtros, ordem]);
  useEffect(() => {
    const alvo = sentinelaRef.current;
    if (!alvo) return;
    const obs = new IntersectionObserver(
      (entradas) => { if (entradas[0]?.isIntersecting) setLimiteLinhas((n) => n + LOTE_RENDERIZACAO); },
      { root: scrollRef.current, rootMargin: '400px' },
    );
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [limiteLinhas, linhas.length]);

  const visiveis = linhas.slice(0, limiteLinhas);

  const filtrosAtivos =
    filtros.busca || filtros.responsavel || filtros.rateio || filtros.robo || filtros.dataDe || filtros.dataAte ||
    filtros.situacoes.length || filtros.urgencias.length;

  const limparFiltros = () => {
    onResponsavelChange('');
    setFiltrosLocais((f) => ({ ...FILTROS_VAZIOS, meses: f.meses }));
  };

  const alternarNaLista = <T,>(lista: T[], valor: T) => (lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor]);

  const aplicarPresetData = (preset: 'vencidas' | 'hoje' | 'semana') => {
    const ontem = chaveData(somarDias(hoje, -1));
    const intervalo =
      preset === 'vencidas' ? { dataDe: '', dataAte: ontem } :
        preset === 'hoje' ? { dataDe: chaveData(hoje), dataAte: chaveData(hoje) } :
          { dataDe: chaveData(hoje), dataAte: chaveData(somarDias(hoje, 7)) };
    setFiltrosLocais((f) => ({ ...f, campoData: 'limite', ...intervalo }));
  };

  const ordenarPor = (campo: CampoOrdem) =>
    setOrdem((o) => (o.campo === campo ? { campo, direcao: o.direcao === 'asc' ? 'desc' : 'asc' } : { campo, direcao: 'asc' }));

  const exportar = () => {
    if (linhas.length === 0) {
      props.onExportado?.(false, 'Nenhum dado para exportar.');
      return;
    }
    try {
      const cabecalho = [
        'UC', 'Outro Número UC', 'Cliente', 'Mês Referência', 'Rateio', 'Status no Rateio', 'Responsável', 'Situação', 'Status da Fatura',
        'Robô Equatorial', 'Urgência', 'Início da Janela', 'Fim da Janela', 'Data Limite', 'Dias até o Limite',
        'Data Prevista', 'Reagendado', 'Tipo de Janela',
      ];
      const dados = linhas.map((l) => {
        const urg = urgenciaDaCobranca(l);
        const robo = statusRoboDaCobranca(l, filaRobo);
        return [
          l.uc || '', l.outro_numero_uc || '', l.nome || '', l.expectedMonth || '', l.rateioNome || '',
          classificarStatusRateio(l.statusRateio)?.rotulo ?? '', l.responsavel_nome || '',
          rotuloSituacao(situacaoDaCobranca(l)), l.fatura?.status_fatura || '',
          robo === 'na_fila' ? 'Na fila'
            : robo === 'falhou' ? ROTULO_MOTIVO_ROBO[motivoDoRobo(filaRobo.get(chaveFilaRobo(l))?.error_details)]
              : '',
          urg ? URGENCIAS.find((u) => u.valor === urg)?.rotulo : '',
          fmt(l.inicioJanelaIso), fmt(l.fimJanelaIso), fmt(l.criticoJanelaIso), diasParaLimite(l, hoje) ?? '',
          fmt(l.targetDateIso), l.hasManualReschedule ? 'Sim' : 'Não',
          l.status === 'sem_previsao' ? 'Sem histórico' : l.isPredictive ? 'Prevista' : 'Real',
        ];
      });
      const ws = XLSX.utils.aoa_to_sheet([cabecalho, ...dados]);
      ws['!cols'] = cabecalho.map((h, i) => ({
        wch: Math.min(50, Math.max(h.length, ...dados.map((r) => String(r[i] ?? '').length)) + 2),
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Janela_Faturamento');
      const agora = new Date();
      XLSX.writeFile(wb, `Janela_Faturamento_Lista_${chaveData(agora)}_${agora.toTimeString().slice(0, 5).replace(':', '')}.xlsx`);
      props.onExportado?.(true, `${linhas.length} cobranças exportadas para Excel.`);
    } catch (e) {
      console.error('Erro ao exportar lista da janela:', e);
      props.onExportado?.(false, 'Erro ao exportar dados para Excel.');
    }
  };

  // Função de render (e não componente) para o <th> não remontar a cada render.
  const Cabecalho = ({ campo, className = '', children }: { campo?: CampoOrdem; className?: string; children: React.ReactNode }) => {
    const ativo = campo && ordem.campo === campo;
    return (
      <th className={`px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap ${className}`}>
        {campo ? (
          <button type="button" onClick={() => ordenarPor(campo)} className={`inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white ${ativo ? 'text-gray-900 dark:text-white' : ''}`}>
            {children}
            {ativo ? (ordem.direcao === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
          </button>
        ) : children}
      </th>
    );
  };

  const selectClass = 'bg-gray-100 dark:bg-gray-700 text-sm border-none rounded-xl px-3 py-2 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-[#5F6C37]';
  const chipBase = 'px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Filtros */}
      <div className="p-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Buscar por UC, cliente ou titular..."
              value={filtros.busca}
              onChange={(e) => setFiltro('busca', e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-gray-100 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-[#5F6C37] outline-none text-gray-800 dark:text-gray-200 text-sm"
            />
          </div>
          <SeletorMeses opcoes={meses} valor={filtros.meses} onChange={(v) => setFiltro('meses', v)} />
          <select value={filtros.responsavel} onChange={(e) => setFiltro('responsavel', e.target.value)} className={selectClass} title="Responsável">
            <option value="">Todos os responsáveis</option>
            {analysts.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
          </select>
          <select value={filtros.rateio} onChange={(e) => setFiltro('rateio', e.target.value)} className={selectClass} title="Rateio">
            <option value="">Todos os rateios</option>
            {rateios.map((r) => <option key={r} value={r}>Rateio {r}</option>)}
          </select>
          <select value={filtros.robo} onChange={(e) => setFiltro('robo', e.target.value as FiltrosLista['robo'])} className={selectClass} title="Fila do robô Equatorial">
            <option value="">Robô: todos</option>
            <option value="na_fila">Robô: na fila</option>
            <option value="falhou">Robô: não baixou</option>
          </select>
          <button
            onClick={exportar}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-green-600/10 shrink-0"
          >
            <Download size={16} /> Exportar Excel
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select value={filtros.campoData} onChange={(e) => setFiltro('campoData', e.target.value as CampoData)} className={selectClass}>
            {CAMPOS_DATA.map((c) => <option key={c.valor} value={c.valor}>{c.rotulo}</option>)}
          </select>
          <span className="text-xs text-gray-400">de</span>
          <input type="date" value={filtros.dataDe} onChange={(e) => setFiltro('dataDe', e.target.value)} className={selectClass} />
          <span className="text-xs text-gray-400">até</span>
          <input type="date" value={filtros.dataAte} onChange={(e) => setFiltro('dataAte', e.target.value)} className={selectClass} />
          <div className="flex items-center gap-1">
            {([['vencidas', 'Limite vencido'], ['hoje', 'Limite hoje'], ['semana', 'Próx. 7 dias']] as const).map(([p, r]) => (
              <button key={p} type="button" onClick={() => aplicarPresetData(p)} className={`${chipBase} border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[#5F6C37] hover:text-[#5F6C37]`}>
                {r}
              </button>
            ))}
          </div>
          <span className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1 hidden md:block" />
          <span className="text-xs font-semibold text-gray-500">Urgência:</span>
          {URGENCIAS.map((u) => {
            const ativo = filtros.urgencias.includes(u.valor);
            return (
              <button
                key={u.valor}
                type="button"
                onClick={() => setFiltro('urgencias', alternarNaLista(filtros.urgencias, u.valor))}
                className={`${chipBase} ${ativo ? `${COR_URGENCIA[u.valor].chip} border-transparent ring-2 ring-[#5F6C37]/40` : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                {u.rotulo}
              </button>
            );
          })}
          {filtrosAtivos ? (
            <button type="button" onClick={limparFiltros} className="ml-auto flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600">
              <FilterX size={14} /> Limpar filtros
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500">Situação:</span>
          <button
            type="button"
            onClick={() => setFiltro('situacoes', filtros.situacoes.length === 2 && filtros.situacoes.every((s) => s === 'sem_fatura' || s === 'a_calcular') ? [] : ['sem_fatura', 'a_calcular'])}
            className={`${chipBase} ${filtros.situacoes.length === 2 && filtros.situacoes.every((s) => s === 'sem_fatura' || s === 'a_calcular') ? 'bg-red-600 text-white border-red-600' : 'border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-900/20'}`}
          >
            Só pendentes ({contagemSituacao.sem_fatura + contagemSituacao.a_calcular})
          </button>
          {SITUACOES.map((s) => {
            const ativo = filtros.situacoes.includes(s.valor);
            const qtd = contagemSituacao[s.valor];
            return (
              <button
                key={s.valor}
                type="button"
                onClick={() => setFiltro('situacoes', alternarNaLista(filtros.situacoes, s.valor))}
                className={`${chipBase} ${ativo ? `${COR_SITUACAO[s.valor]} border-transparent ring-2 ring-[#5F6C37]/40` : `border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${qtd === 0 ? 'opacity-50' : ''}`}`}
              >
                {s.rotulo} <span className="opacity-70">({qtd})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Resumo */}
      <div className="px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50/60 dark:bg-gray-900/20 border-b dark:border-gray-700 shrink-0">
        <span><strong className="text-gray-900 dark:text-white">{linhas.length}</strong> de {itens.length} cobranças</span>
        <span className="flex items-center gap-1 text-red-600 font-semibold"><Clock size={12} /> {resumo.pendentes} pendentes</span>
        <span className="text-red-600">{resumo.criticas} críticas</span>
        <span>{resumo.vencidas} com limite vencido</span>
        {ordem.campo !== 'prioridade' && (
          <button type="button" onClick={() => setOrdem(ORDEM_PADRAO)} className="text-[#5F6C37] font-semibold hover:underline">
            Voltar à ordem de prioridade
          </button>
        )}
      </div>

      {/* Tabela */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto custom-scrollbar bg-white dark:bg-gray-800">
        {linhas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <CheckCircle2 size={48} className="mb-4 text-[#5F6C37] opacity-20" />
            <p className="text-lg font-medium">Nenhuma cobrança encontrada</p>
            <p className="text-sm">Ajuste os filtros ou o mês de referência.</p>
          </div>
        ) : (
          <table className="w-full min-w-[1280px] text-sm border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 shadow-[0_1px_0_0_rgb(229,231,235)] dark:shadow-[0_1px_0_0_rgb(55,65,81)]">
              <tr>
                {Cabecalho({ campo: 'prioridade', className: 'pl-4', children: 'Prior.' })}
                {Cabecalho({ campo: 'uc', children: 'UC' })}
                {Cabecalho({ campo: 'cliente', children: 'Cliente' })}
                {Cabecalho({ campo: 'mes', children: 'Mês ref.' })}
                {Cabecalho({ children: 'Rateio' })}
                {Cabecalho({ campo: 'responsavel', children: 'Responsável' })}
                {Cabecalho({ campo: 'situacao', children: 'Situação' })}
                {Cabecalho({ campo: 'inicio', children: 'Janela' })}
                {Cabecalho({ campo: 'limite', children: 'Data limite' })}
                {Cabecalho({ campo: 'prevista', children: 'Prevista' })}
                {Cabecalho({ className: 'text-right pr-4 sticky right-0 bg-gray-50 dark:bg-gray-900', children: 'Ações' })}
              </tr>
            </thead>
            <tbody>
              {visiveis.map((item) => (
                <LinhaCobranca key={`${item.uc}-${item.expectedMonth}`} item={item} hoje={hoje} {...props} />
              ))}
              {visiveis.length < linhas.length && (
                <tr ref={sentinelaRef}>
                  <td colSpan={11} className="py-4 text-center text-xs text-gray-400">
                    <Loader2 size={14} className="inline animate-spin mr-1" /> Carregando mais ({visiveis.length} de {linhas.length})…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Linha ────────────────────────────────────────────────────────────────────

const LinhaCobranca: React.FC<Props & { item: any; hoje: Date }> = ({
  item, hoje, filaRobo, enqueuingForUc, uploadingForUc, uploadSuccessUc, podeTransferir,
  onCalcular, onVisualizar, onReagendar, onVerReagendamento, onTransferir, onPular, onEquatorial, onRetentarRobo, onVerHistoricoRobo,
  onAtualizarDadosTitular, onUpload,
}) => {
  const situacao = situacaoDaCobranca(item);
  const urgencia = urgenciaDaCobranca(item);
  const pendente = !item.isCalculada;
  const dias = pendente ? diasParaLimite(item, hoje) : null;
  // O robô só aparece enquanto falta a fatura (ver statusRoboDaCobranca).
  const filaEntrada = pendente && !item.hasFatura ? filaRobo.get(chaveFilaRobo(item)) : null;
  const robo = statusRoboDaCobranca(item, filaRobo);
  const motivoRobo = robo === 'falhou' ? motivoDoRobo(filaEntrada?.error_details) : null;
  const semHistorico = item.status === 'sem_previsao';
  const td = 'px-3 py-2 border-b border-gray-100 dark:border-gray-700/60 align-middle';
  const iconBtn = 'p-1.5 rounded-lg text-gray-400 transition-colors disabled:opacity-40';

  const botaoRobo = () => {
    if (item.hasFatura) return null;
    if (robo === 'na_fila') {
      return (
        <span title="Aguardando o robô" className="px-2 py-1 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 rounded-lg text-[11px] font-bold flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Robô
        </span>
      );
    }
    // Cadastro recusado pela Equatorial: retentar só depois de corrigir.
    if (motivoRobo === 'dados_incorretos' && !dadosTitularMudaram(filaEntrada, item.ucData)) {
      return (
        <button
          onClick={() => onAtualizarDadosTitular(item, filaEntrada)}
          title="A Equatorial recusou o CPF/CNPJ ou a data de nascimento. Corrija o cadastro para liberar uma nova busca."
          className="px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1 whitespace-nowrap bg-orange-500 hover:bg-orange-600 text-white"
        >
          <UserCog size={12} /> Atualizar dados
        </button>
      );
    }
    if (motivoRobo) {
      return (
        <button
          onClick={() => onRetentarRobo(filaEntrada.id, item)}
          title={motivoRobo === 'dados_incorretos'
            ? 'Cadastro corrigido — clique para o robô buscar com os dados novos'
            : `${ROTULO_MOTIVO_ROBO[motivoRobo]}: ${filaEntrada?.error_details || 'sem detalhes'} — clique para o robô buscar de novo`}
          className="px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1 border border-gray-200 text-gray-600 hover:border-green-600 hover:text-green-700 dark:border-gray-600 dark:text-gray-300 dark:hover:text-green-400"
        >
          <RotateCw size={12} /> Retentar
        </button>
      );
    }
    const { temDoc, temData, completo } = credenciaisRobo(item);
    const enviando = enqueuingForUc === item.uc_identificadora;
    return (
      <button
        onClick={() => completo && onEquatorial(item, semHistorico ? 'ultima_disponivel' : 'mes_especifico')}
        disabled={!completo || enviando}
        title={!temDoc ? 'Falta CPF/CNPJ na UC' : !temData ? 'Falta data de nascimento na UC' : semHistorico ? 'Buscar a última fatura disponível' : 'Baixar da Equatorial (prioridade na fila do robô)'}
        className={`px-2 py-1 rounded-lg text-[11px] font-bold flex items-center gap-1 whitespace-nowrap ${completo ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed dark:bg-gray-700 dark:border-gray-600'}`}
      >
        {enviando ? <Loader2 size={12} className="animate-spin" /> : completo ? <Bot size={12} /> : <AlertTriangle size={12} />}
        {semHistorico ? 'Última fatura' : 'Equatorial'}
      </button>
    );
  };

  return (
    <tr className={`group hover:bg-gray-50 dark:hover:bg-gray-700/30 ${pendente ? '' : 'text-gray-500 dark:text-gray-400'}`}>
      <td className={`${td} pl-4`}>
        {urgencia ? (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${COR_URGENCIA[urgencia].chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${COR_URGENCIA[urgencia].barra}`} />
            {URGENCIAS.find((u) => u.valor === urgencia)?.rotulo}
          </span>
        ) : (
          <CheckCircle2 size={16} className="text-emerald-500 ml-2" />
        )}
      </td>
      <td className={td}>
        <div className="font-mono text-xs font-bold text-[#5F6C37] dark:text-green-300 whitespace-nowrap">{item.uc}</div>
        {item.outro_numero_uc && <div className="font-mono text-[11px] text-gray-400 whitespace-nowrap">{item.outro_numero_uc}</div>}
      </td>
      <td className={`${td} max-w-[260px]`}>
        <div className={`truncate font-medium ${pendente ? 'text-gray-900 dark:text-white' : ''}`} title={item.nome}>{item.nome || '—'}</div>
      </td>
      <td className={`${td} whitespace-nowrap text-xs font-semibold`}>{item.expectedMonth}</td>
      <td className={td}>
        <div className="flex flex-col items-start gap-0.5">
          <span className="whitespace-nowrap text-xs">{item.rateioNome || '—'}</span>
          <ChipStatusRateio status={item.statusRateio} rateio={item.rateioNome} />
        </div>
      </td>
      <td className={`${td} max-w-[180px]`}>
        <div className="truncate text-xs" title={item.responsavel_nome}>{item.responsavel_nome || '—'}</div>
      </td>
      <td className={td}>
        <div className="flex flex-wrap items-center gap-1">
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${COR_SITUACAO[situacao]}`}
            title={item.fatura?.status_fatura ? `Status da fatura: ${item.fatura.status_fatura}` : 'Nenhuma fatura recebida para o mês'}
          >
            {rotuloSituacao(situacao)}
          </span>
          {item.hasManualReschedule && (
            <button
              type="button"
              onClick={() => item.fatura?.reagendamento_em && onVerReagendamento(item)}
              className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300 flex items-center gap-0.5"
              title="Cobrança reagendada — ver detalhes"
            >
              <Calendar size={10} /> Reagendada
            </button>
          )}
          {motivoRobo && <ChipMotivoRobo motivo={motivoRobo} detalhe={filaEntrada?.error_details} />}
          {item.atrasadaEquatorial ? <ChipFaturaAtrasada previsao={item.previsaoFatura} />
            : pendente && !item.hasFatura && item.previsaoFatura?.perfil === 'impressa_na_hora' ? <ChipImpressaNaHora /> : null}
          {item.prazoCurto && !item.atrasadaEquatorial ? (
            <ChipPrazoCurto chegada={item.previsaoFatura?.chegada} limite={chaveData(item.criticoJanelaIso)} />
          ) : null}
        </div>
      </td>
      <td className={`${td} whitespace-nowrap text-xs`}>
        {item.inicioJanelaIso || item.fimJanelaIso ? (
          <span
            title={!item.isPredictive ? 'Janela da fatura recebida'
              : item.previsaoFatura?.perfil === 'impressa_na_hora' ? 'Janela prevista: fatura impressa na hora, costuma existir no dia da leitura'
                : item.previsaoFatura ? 'Janela prevista: a fatura costuma chegar no 4º dia útil depois da leitura e é apresentada no 7º'
                  : 'Janela prevista pelo histórico'}
          >
            {fmt(item.inicioJanelaIso)} <span className="text-gray-400">→</span> {fmt(item.fimJanelaIso)}
            {item.isPredictive && <span className="ml-1 text-[10px] text-blue-500 font-semibold">prev.</span>}
          </span>
        ) : (
          <span className="text-gray-400 italic">Sem histórico</span>
        )}
      </td>
      <td className={`${td} whitespace-nowrap`}>
        <div className={`text-xs font-bold ${urgencia === 'critical' ? 'text-red-600' : urgencia === 'warning' ? 'text-amber-600' : ''}`}>
          {fmt(item.criticoJanelaIso)}
        </div>
        {dias !== null && (
          <div className={`text-[11px] ${dias < 0 ? 'text-red-500 font-semibold' : dias <= 2 ? 'text-amber-600' : 'text-gray-400'}`}>
            {dias < 0 ? `há ${-dias}d` : dias === 0 ? 'hoje' : `em ${dias}d`}
          </div>
        )}
      </td>
      <td className={`${td} whitespace-nowrap text-xs`}>{fmt(item.targetDateIso)}</td>
      {/* Fixa na direita: em tela estreita a tabela rola na horizontal e as ações somem. */}
      <td className={`${td} pr-4 sticky right-0 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700`}>
        <div className="flex items-center justify-end gap-1">
          {pendente ? (
            <>
              <button onClick={() => onReagendar(item)} title="Reagendar" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20`}>
                <Calendar size={15} />
              </button>
              {podeTransferir(item) && (
                <button onClick={() => onTransferir(item)} title="Transferir responsável" className={`${iconBtn} hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20`}>
                  <ArrowRightLeft size={15} />
                </button>
              )}
              <button onClick={() => onPular(item)} title="Não precisa calcular" className={`${iconBtn} hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20`}>
                <SkipForward size={15} />
              </button>
              <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5" />
              {Array.isArray(filaEntrada?.historico_execucoes) && filaEntrada.historico_execucoes.length > 0 && (
                <button
                  onClick={() => onVerHistoricoRobo(filaEntrada)}
                  title="Ver relatório de tentativas do robô"
                  className={`${iconBtn} hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700`}
                >
                  <History size={15} />
                </button>
              )}
              {botaoRobo()}
              {item.hasFatura ? (
                <button
                  onClick={() => onCalcular(item)}
                  className={`px-2.5 py-1 text-white rounded-lg text-[11px] font-bold flex items-center gap-0.5 whitespace-nowrap ${urgencia === 'critical' ? 'bg-red-500 hover:bg-red-600' : urgencia === 'warning' || urgencia === 'sem_previsao' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#5F6C37] hover:bg-[#5F6C37]/90'}`}
                >
                  Calcular <ChevronRight size={12} />
                </button>
              ) : uploadSuccessUc === item.uc_identificadora ? (
                <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg text-[11px] font-bold flex items-center gap-1">
                  <CheckCircle2 size={12} /> Na fila
                </span>
              ) : (
                <button
                  onClick={() => onUpload(item.uc_identificadora)}
                  disabled={uploadingForUc === item.uc_identificadora}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 disabled:opacity-50 whitespace-nowrap"
                >
                  {uploadingForUc === item.uc_identificadora ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                </button>
              )}
            </>
          ) : item.fatura?.id ? (
            <button
              onClick={() => onVisualizar(item)}
              className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1"
            >
              <Eye size={12} /> Visualizar
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
};

export default JanelaFaturamentoLista;
